import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { toast } from 'sonner'
import { UIState, UIActions, Notification } from '../types/ui'
import { storage as indexedDBStorage } from '../utils/indexedDBStorage'

/**
 * 生成唯一ID
 */
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

/**
 * UI管理 Store
 */
interface UIStore extends UIState, UIActions {}

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      // 布局状态
      isSidebarVisible: true,
      isPreviewVisible: true,
      sidebarWidth: 256,
      isEditorFocused: false,
      
      // 模态框状态
      isExportModalOpen: false,
      isSettingsModalOpen: false,
      isNewDocumentModalOpen: false,
      isDeleteConfirmModalOpen: false,
      
      // 编辑器状态
      editorFontSize: 14,
      editorTabSize: 2,
      editorWordWrap: true,
      editorLineNumbers: true,
      
      // 通知状态
      notifications: [],
      
      // 加载状态
      isLoading: false,
      loadingMessage: undefined,

      // 侧边栏操作
      toggleSidebar: () => {
        set((state) => ({
          isSidebarVisible: !state.isSidebarVisible
        }))
      },
      
      setSidebarVisible: (visible: boolean) => {
        set({ isSidebarVisible: visible })
      },
      
      setSidebarWidth: (width: number) => {
        // 限制侧边栏宽度在合理范围内
        const clampedWidth = Math.max(200, Math.min(500, width))
        set({ sidebarWidth: clampedWidth })
      },

      // 预览操作
      togglePreview: () => {
        set((state) => ({
          isPreviewVisible: !state.isPreviewVisible
        }))
      },
      
      setPreviewVisible: (visible: boolean) => {
        set({ isPreviewVisible: visible })
      },

      // 模态框操作
      openExportModal: () => {
        set({ isExportModalOpen: true })
      },
      
      closeExportModal: () => {
        set({ isExportModalOpen: false })
      },
      
      openSettingsModal: () => {
        set({ isSettingsModalOpen: true })
      },
      
      closeSettingsModal: () => {
        set({ isSettingsModalOpen: false })
      },
      
      openNewDocumentModal: () => {
        set({ isNewDocumentModalOpen: true })
      },
      
      closeNewDocumentModal: () => {
        set({ isNewDocumentModalOpen: false })
      },
      
      openDeleteConfirmModal: () => {
        set({ isDeleteConfirmModalOpen: true })
      },
      
      closeDeleteConfirmModal: () => {
        set({ isDeleteConfirmModalOpen: false })
      },

      // 编辑器操作
      setEditorFontSize: (size: number) => {
        // 限制字体大小在合理范围内
        const clampedSize = Math.max(10, Math.min(24, size))
        set({ editorFontSize: clampedSize })
      },
      
      setEditorTabSize: (size: number) => {
        // 限制Tab大小在合理范围内
        const clampedSize = Math.max(1, Math.min(8, size))
        set({ editorTabSize: clampedSize })
      },
      
      setEditorWordWrap: (enabled: boolean) => {
        set({ editorWordWrap: enabled })
      },
      
      setEditorLineNumbers: (enabled: boolean) => {
        set({ editorLineNumbers: enabled })
      },
      
      setEditorFocused: (focused: boolean) => {
        set({ isEditorFocused: focused })
      },

      // 通知操作
      addNotification: (notification: Omit<Notification, 'id'>) => {
        const id = generateId()
        const newNotification: Notification = {
          ...notification,
          id,
          timestamp: Date.now()
        }
        
        set((state) => ({
          notifications: [...state.notifications, newNotification]
        }))
        
        // 自动移除通知
        if (notification.duration !== 0) {
          setTimeout(() => {
            get().removeNotification(id)
          }, notification.duration || 5000)
        }
      },
      
      removeNotification: (id: string) => {
        set((state) => ({
          notifications: state.notifications.filter(n => n.id !== id)
        }))
      },
      
      clearNotifications: () => {
        set({ notifications: [] })
      },

      // 加载状态操作
      setLoading: (loading: boolean, message?: string) => {
        set({ 
          isLoading: loading,
          loadingMessage: loading ? message : undefined
        })
      }
    }),
    {
      name: 'markflow-ui',
      storage: createJSONStorage(() => indexedDBStorage),
      partialize: (state) => ({
        isSidebarVisible: state.isSidebarVisible,
        sidebarWidth: state.sidebarWidth,
        isPreviewVisible: state.isPreviewVisible,
        editorFontSize: state.editorFontSize,
        editorTabSize: state.editorTabSize,
        editorWordWrap: state.editorWordWrap,
        editorLineNumbers: state.editorLineNumbers
      })
    }
  )
)

/**
 * 通知辅助函数
 */
export const showSuccess = (title: string, message?: string) => {
  toast.success(title, { description: message })
  
  useUIStore.getState().addNotification({
    type: 'success',
    title,
    message
  })
}

export const showError = (title: string, message?: string) => {
  toast.error(title, { description: message })

  useUIStore.getState().addNotification({
    type: 'error',
    title,
    message,
    duration: 8000 // 错误消息显示更长时间
  })
}

export const showWarning = (title: string, message?: string) => {
  toast.warning(title, { description: message })

  useUIStore.getState().addNotification({
    type: 'warning',
    title,
    message
  })
}

export const showInfo = (title: string, message?: string) => {
  toast.info(title, { description: message })

  useUIStore.getState().addNotification({
    type: 'info',
    title,
    message
  })
}

/**
 * 键盘快捷键辅助函数
 */
export const setupKeyboardShortcuts = () => {
  const handleKeyDown = (event: KeyboardEvent) => {
    const { metaKey, ctrlKey, altKey, shiftKey, key } = event
    const cmdOrCtrl = metaKey || ctrlKey
    
    // Ctrl/Cmd + N: 新建文档
    if (cmdOrCtrl && key === 'n' && !shiftKey && !altKey) {
      event.preventDefault()
      useUIStore.getState().openNewDocumentModal()
    }
    
    // Ctrl/Cmd + S: 保存文档
    if (cmdOrCtrl && key === 's' && !shiftKey && !altKey) {
      event.preventDefault()
      // 这里会调用文档保存逻辑
    }
    
    // Ctrl/Cmd + E: 导出
    if (cmdOrCtrl && key === 'e' && !shiftKey && !altKey) {
      event.preventDefault()
      useUIStore.getState().openExportModal()
    }
    
    // Ctrl/Cmd + B: 切换侧边栏
    if (cmdOrCtrl && key === 'b' && !shiftKey && !altKey) {
      event.preventDefault()
      useUIStore.getState().toggleSidebar()
    }
    
    // Ctrl/Cmd + P: 切换预览
    if (cmdOrCtrl && key === 'p' && !shiftKey && !altKey) {
      event.preventDefault()
      useUIStore.getState().togglePreview()
    }
    
    // Ctrl/Cmd + ,: 设置
    if (cmdOrCtrl && key === ',' && !shiftKey && !altKey) {
      event.preventDefault()
      useUIStore.getState().openSettingsModal()
    }
    
    // Escape: 关闭模态框
    if (key === 'Escape' && !cmdOrCtrl && !shiftKey && !altKey) {
      const state = useUIStore.getState()
      if (state.isExportModalOpen) {
        event.preventDefault()
        state.closeExportModal()
      } else if (state.isSettingsModalOpen) {
        event.preventDefault()
        state.closeSettingsModal()
      } else if (state.isNewDocumentModalOpen) {
        event.preventDefault()
        state.closeNewDocumentModal()
      } else if (state.isDeleteConfirmModalOpen) {
        event.preventDefault()
        state.closeDeleteConfirmModal()
      }
    }
  }
  
  document.addEventListener('keydown', handleKeyDown)
  
  // 返回清理函数
  return () => {
    document.removeEventListener('keydown', handleKeyDown)
  }
}

/**
 * 初始化UI状态
 */
export const initializeUI = () => {
  setupKeyboardShortcuts()
  
  // 监听窗口大小变化
  const handleResize = () => {
    const width = window.innerWidth
    const state = useUIStore.getState()
    
    // 在小屏幕上自动隐藏侧边栏
    if (width < 768 && state.isSidebarVisible) {
      state.setSidebarVisible(false)
    }
  }
  
  window.addEventListener('resize', handleResize)
  handleResize() // 初始检查
  
  return () => {
    window.removeEventListener('resize', handleResize)
  }
}